import React, { ReactElement, useEffect, useState } from "react";
import { Tabs, Input, Popover, Table } from "antd";
import { Hooks, Util, Platform, Storage } from "@xt/xtbase";
import cs from "./index.module.less";
import { VipInfoObj, FuVipInfoObj } from "../common";
import SvgIcon from "@xt/SvgIcon";
import XtPane from "@/components/App/XtPane";
import { getCurrencyRate } from "@/api/v4/wallet";
import type { ColumnsType } from 'antd/es/table';
import icon404 from "@/assets/images/common/icon404.png";
import { useStore } from '@/models';
import { observer } from 'mobx-react-lite';
const vInfoObj = new VipInfoObj(null);
const fuvInfoObj = new FuVipInfoObj(null);
const {useTranslation} = Hooks;
const {Big} = Util;


interface PropType {
    vipList: typeof vInfoObj[];
    fuVipList: typeof fuvInfoObj[];
    rateType: number | string;
    layout?: "tabs" | "singlePage";
}
interface ChainsType {
    chain: string, //网络类型
    withdrawEnabled: boolean, //是否支持提现，true：支持，false：不支持
    depositEnabled: boolean, //是否支持充值，true：支持，false：不支持
    withdrawFeeAmount: number, //单笔提币手续费
    depositFeeRate: number, //充值费率，0表示FREE
    withdrawMinAmount: number, // 最小提币数量
}
interface CurrencyRatesType_copy {
    currency: string,
    dailyWithdrawAmount: number, //单日提币限额
    maxWithdrawAmount: number, //单笔提币限额
    logo?: string,
    chains: ChainsType[]
}

interface CurrencyRatesType {
    currency: string,
    dailyWithdrawAmount: number, //单日提币限额
    maxWithdrawAmount: number, //单笔提币限额
    logo?: string,
    chain: string, //网络类型
    withdrawEnabled: boolean, //是否支持提现，true：支持，false：不支持
    depositEnabled: boolean, //是否支持充值，true：支持，false：不支持
    withdrawFeeAmount: number, //单笔提币手续费
    depositFeeRate: number, //充值费率，0表示FREE
    withdrawMinAmount: number, // 最小提币数量
    state: string,
    bothAmount: string,
    rowSpanNum: number
}

const RateTable: React.FC<PropType> = ({ vipList, fuVipList, rateType, layout = "tabs" }): ReactElement => {
    const t = useTranslation();
    const [currencyRates, setCurrencyRates] = useState<CurrencyRatesType[]>();
    const [currencyRatesCopy, setCurrencyRatesCopy] = useState<CurrencyRatesType[]>();
    const [keyWord, setKeyWord] = useState<string>("");
    const [tabIndex, setTabIndex] = useState<string>("1"); 
    const isInApp = Platform.isInApp();
    const { fund } = useStore();

    const makerTakerContent = () => {
        return <>
            <strong>{ t("group.WhatIsMaker") }</strong>
            <p>{ t("group.MakerExplain") }</p>
            <strong>{ t("group.WhatIsTaker") }</strong>
            <p>{ t("group.TakerExplain") }</p>
        </>
    };

    const makerContent = () => {
        return <>
            <strong>{ t("group.WhatIsMaker") }</strong>
            <p>{ t("group.MakerExplain") }</p>
        </>
    };

    const takerContent = () => {
        return <>
            <strong>{ t("group.WhatIsTaker") }</strong>
            <p>{ t("group.TakerExplain") }</p>
        </>
    };

    useEffect(() => {
        if (layout === "singlePage") return;
        getCurrencyRate({ headers: { device: isInApp ? "app" : "web" } })
            .then(res => {
                if (!Array.isArray(res)) return;
                const currencies = fund.currencyList;
                const result = res
                    .map(item => {
                        const coin = currencies.find(c => c.currency === item.currency);
                        if (!coin) return null;
                        return {
                            ...item,
                            logo: coin.logo,
                            weight: coin.weight ?? -999999,
                        };
                    })
                    .filter((x): x is NonNullable<typeof x> => x != null);
                const sortResult = result.sort((a, b) => {
                    return b.weight - a.weight;
                });
                const handleArr: CurrencyRatesType[] = [];
                sortResult.forEach(item => {
                    const { currency, chains, dailyWithdrawAmount, maxWithdrawAmount, logo } = item;
                    chains.forEach((chain, i) => {
                        const { withdrawEnabled, depositEnabled } = chain;
                        let cStatus: string = "";
                        if (!withdrawEnabled && !depositEnabled) cStatus = t("pause");
                        if (withdrawEnabled && !depositEnabled) cStatus = t("depositPause");
                        if (!withdrawEnabled && depositEnabled) cStatus = t("withdrawPause");
                        handleArr.push({
                            rowSpanNum: i == 0 ? chains.length : 0,
                            currency,
                            dailyWithdrawAmount,
                            maxWithdrawAmount,
                            logo,
                            state: cStatus,
                            bothAmount: `${!isNaN(maxWithdrawAmount) ? Big(maxWithdrawAmount || 0).toFixedMaxCy(8) : 0} / ${!isNaN(dailyWithdrawAmount) ? Big(dailyWithdrawAmount || 0).toFixedMaxCy(8) : 0}`,
                            ...chain,
                        });
                    });
                });
                setCurrencyRates(handleArr);
                setCurrencyRatesCopy(handleArr);
            })
            .catch(() => {});
    }, [layout]);

    useEffect(() => {
        const filterArr = currencyRatesCopy?.filter(item => {
            return item.currency.indexOf(keyWord.toLowerCase()) != -1
        });
        setCurrencyRates(filterArr);
    }, [keyWord, currencyRatesCopy]);

    const onKeyWordChange = (e) => {
        setKeyWord(e.target.value + "");
    };

    const onTabChange = (i) => {
        setTabIndex(i);
    }

    const rateColumns: ColumnsType<CurrencyRatesType> = [
        {
            title: t("mainChainType"),
            dataIndex: "chain",
            width: 130,
            render: (text, record) => <>{text} {record.state && <div className="cellState">{record.state}</div>} </>,
        },
        {
            title: t("depositFeeRate"),
            dataIndex: "depositFeeRate",
            width: 80,
            render: (text) => <>{+text === 0 ? "Free" : text}</>
        },
        {
            title: t("withdrawFeeRate"),
            dataIndex: "withdrawFeeAmount",
            width: 100,
            render: (text) => <>{Big(+text || 0).toFixedMaxCy(8)}</>
        },
        {
            title: t("withdrawCoinNum"),
            dataIndex: "withdrawMinAmount",
            width: 100,
            render: (text) => <>{Big(+text || 0).toFixedMaxCy(8)}</>
        },
        {
            title: t("withdrawMaxFeeRate"),
            dataIndex: "bothAmount",
            width: 150,
            align: 'right',
            onCell: (item: any, index) => ({
                rowSpan: item.rowSpanNum,
            }),
        }
    ]

    const cacheTable = React.useMemo(() => <Table className="rateCurrencyTable" columns={rateColumns} dataSource={currencyRates} pagination={false} scroll={isInApp ? { x: 1000 } : { x: 1000, y: 865 }} rowClassName="rateCurrencyTableRow" />, [currencyRates]);

    const withdrawalLimitData = React.useMemo(() => {
        if (!currencyRatesCopy?.length) return [];
        const map = new Map<string, { currency: string; logo?: string; maxWithdrawAmount: number; dailyWithdrawAmount: number }>();
        currencyRatesCopy.forEach(item => {
            if (item.rowSpanNum > 0 && !map.has(item.currency)) {
                map.set(item.currency, {
                    currency: item.currency,
                    logo: item.logo,
                    maxWithdrawAmount: item.maxWithdrawAmount,
                    dailyWithdrawAmount: item.dailyWithdrawAmount,
                });
            }
        });
        return Array.from(map.values());
    }, [currencyRatesCopy]);

    const limitColumns: ColumnsType<{ currency: string; logo?: string; maxWithdrawAmount: number; dailyWithdrawAmount: number }> = React.useMemo(() => [
        { title: t("coinType"), dataIndex: "currency", key: "currency", width: 80, render: (text: string, record: any) => <div className="currencyName">{<img src={record.logo || icon404} />}{text.toUpperCase()}</div> },
        { title: t("rate.singleLimit"), dataIndex: "maxWithdrawAmount", key: "max", width: 120, render: (v: number) => (v != null && !isNaN(v) ? Big(v).toFixedMaxCy(8) : "-") },
        { title: t("rate.dailyLimit"), dataIndex: "dailyWithdrawAmount", key: "daily", width: 120, render: (v: number) => (v != null && !isNaN(v) ? Big(v).toFixedMaxCy(8) : "-") },
    ], [t]);

    const limitTable = React.useMemo(() => <Table className="rateCurrencyTable" columns={limitColumns} dataSource={withdrawalLimitData} pagination={false} rowKey="currency" />, [withdrawalLimitData, limitColumns]);

    // 写死数据，与设计稿一致，一屏展示不滑动
    const freeLabel = t("rate.free");
    const CRYPTO_ROWS = [
        { chain: "Ethereum", depositRate: freeLabel, withdrawFee: "1 USDT" },
        { chain: "BSC", depositRate: freeLabel, withdrawFee: "1 USDT" },
        { chain: "Tron", depositRate: freeLabel, withdrawFee: "4 USDT" },
    ];
    const FIAT_ROWS = [
        { currency: "AED", depositRate: freeLabel, withdrawRate: "5%" },
        { currency: "BRL", depositRate: freeLabel, withdrawRate: "3%" },
        { currency: "SAR", depositRate: freeLabel, withdrawRate: "5%" },
    ];
    const LIMIT_ROWS = [
        { currency: "USDT", single: "-", daily: "1000000" },
        { currency: "AED", single: "-", daily: "1000000" },
        { currency: "BRL", single: "-", daily: "1000000" },
        { currency: "SAR", single: "-", daily: "1000000" },
    ];

    const singlePageContent = (
        <div className={cs.singlePageWrap}>
            <div className={cs.section}>
                <div className={cs.sectionTitleRow}>
                    <span className={cs.sectionIconDollar}>$</span>
                    <p className={cs.sectionTitle}>{t("rate.rechargeRate")}</p>
                </div>
                <div className={cs.sectionBlock}>
                    <p className={cs.sectionTitle}>{t("rate.cryptoTable")}</p>
                    <div className={cs.staticTable}>
                        <div className={cs.staticTableRow}>
                            <div className={cs.staticCell}>{t("mainChainType")}</div>
                            <div className={cs.staticCell}>{t("depositFeeRate")}</div>
                            <div className={cs.staticCell}>{t("withdrawFeeRate")}</div>
                        </div>
                        {CRYPTO_ROWS.map((row, i) => (
                            <div className={cs.staticTableRow} key={i}>
                                <div className={cs.staticCell}>{row.chain}</div>
                                <div className={cs.staticCell}>{row.depositRate}</div>
                                <div className={cs.staticCell}>{row.withdrawFee}</div>
                            </div>
                        ))}
                    </div>
                </div>
                <div className={cs.sectionBlock}>
                    <p className={cs.sectionTitle}>{t("rate.fiatTable")}</p>
                    <div className={cs.staticTable}>
                        <div className={cs.staticTableRow}>
                            <div className={cs.staticCell}>{t("coinType")}</div>
                            <div className={cs.staticCell}>{t("depositFeeRate")}</div>
                            <div className={cs.staticCell}>{t("rate.withdrawRate")}</div>
                        </div>
                        {FIAT_ROWS.map((row, i) => (
                            <div className={cs.staticTableRow} key={i}>
                                <div className={cs.staticCell}>{row.currency}</div>
                                <div className={cs.staticCell}>{row.depositRate}</div>
                                <div className={cs.staticCell}>{row.withdrawRate}</div>
                            </div>
                        ))}
                    </div>
                </div>
                <div className={cs.sectionBlock}>
                <p className={cs.sectionTitle}>{t("rate.withdrawLimit")}</p>
                    <div className={cs.staticTable}>
                        <div className={cs.staticTableRow}>
                            <div className={cs.staticCell}>{t("coinType")}</div>
                            <div className={cs.staticCell}>{t("rate.singleLimit")}</div>
                            <div className={cs.staticCell}>{t("rate.dailyLimit")}</div>
                        </div>
                        {LIMIT_ROWS.map((row, i) => (
                            <div className={cs.staticTableRow} key={i}>
                                <div className={cs.staticCell}>{row.currency}</div>
                                <div className={cs.staticCell}>{row.single}</div>
                                <div className={cs.staticCell}>{row.daily}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );

    const normalVipTable = (
        <Tabs defaultActiveKey="1"  onChange={onTabChange} >
            <Tabs.TabPane tab={t("spotFee")} key="1" forceRender>
               
                <div className={`${cs.table} ${cs.coinTable}`}>
                    <ul className={cs.header}>
                        <li>{ t("group.Level") }</li>
                        <li>{ t("group.tradeVolumnMonth") }</li>
                        <li>{ t("group.andOr") }</li>
                        <li>{ t("group.xtHoldVolumn") }</li>
                        <li>
                            <span className={cs.makerTakerTips}>{t("trade.makerTaker")}(%)</span>
                            <Popover content={makerTakerContent} overlayClassName="contractTableTip" placement="left" trigger="hover">
                                <span className={cs.appMakerTakerTips}>
                                    {t("trade.makerTaker")}(%)
                                </span>
                            </Popover>
                        </li>
                    </ul>
                    {/* @ts-ignore */}
                    <XtPane minHeight={930} nodata={!Array.isArray(vipList) || vipList.length <= 0}>
                        <div className={cs.tbody}>
                            { Array.isArray(vipList) && vipList.map((item, index) => {
                                
                                return <ul key={index}>
                                    <li>{item.curLevelName}</li>
                                    <li><span>≥</span>{item.tradeVolumeStr}</li>
                                    <li>{ t("group.and") }</li>
                                    <li><span>≥</span>{item.xtVolumeStr}</li>
                                    <li>{item.makerFeeRate}/{item.takerFeeRate}</li>
                                </ul>
                            })}
                        </div>
                    </XtPane>
                </div>
            </Tabs.TabPane>
            <Tabs.TabPane tab={t("rate.rechargeRate")} key="2" forceRender>
                <div className={cs.sectionBlock}>
                    <p className={cs.sectionTitle}>{t("rate.cryptoTable")}</p>
                    <div className={cs.searchView}>
                        <Input
                            placeholder={t("inputCoinType")}
                            prefix={<SvgIcon icon="search" style={{ color: "rgba(255, 255, 255, 0.56)" }} />}
                            onChange={onKeyWordChange}
                        />
                    </div>
                    {cacheTable}
                </div>
                {/* <div className={`${cs.table} ${cs.currencyTable}`}>
                    <div className={cs.currencyHeader}>
                        <div className={cs.firstCell}>{ t("coinType") }</div>
                        <div>
                            <ul className={cs.chainsItem}>
                                <li>{ t("mainChainType") }</li>
                                <li>{ t("depositFeeRate") }</li>
                                <li>{ t("withdrawFeeRate") }</li>
                                <li>{ t("withdrawCoinNum") }</li>
                            </ul>
                        </div>
                        <div>{ t("withdrawMaxFeeRate") }</div>
                        
                    </div>
                    <XtPane minHeight={930} nodata={!Array.isArray(vipList) || vipList.length <= 0}>
                        <div className={cs.tbody}>
                            { Array.isArray(currencyRates) && currencyRates.filter(item=>{return item.currency.indexOf(keyWord.toLowerCase()) != -1}).map((item) => {
                                
                                return <div key={item.currency} className={cs.currencyItem}>
                                    <div  className={cs.firstCell}>{item.logo && <img src={item.logo}/> }{item.currency?.toUpperCase()}</div>
                                    <div>
                                        {item.chains.map((cItem, idx) => {
                                            const {withdrawEnabled, depositEnabled} = cItem;
                                            let cStatus: string = "";
                                            if (!withdrawEnabled && !depositEnabled) {
                                                cStatus = t("pause");
                                            }
                                            if (withdrawEnabled && !depositEnabled) {
                                                cStatus = t("depositPause");
                                            }
                                            if (!withdrawEnabled && depositEnabled) {
                                                cStatus = t("withdrawPause");
                                            }
                                            return <ul className={cs.chainsItem} key={idx}>
                                                <li>{cItem.chain} {cStatus && <div className={cs.status}>{ cStatus }</div>} </li>
                                                <li>{cItem.depositFeeRate === 0 ? "Free" : cItem.depositFeeRate}</li>
                                                <li>{Big(cItem?.withdrawFeeAmount || 0).toFixedMaxCy(8)}</li>
                                                <li>{Big(cItem?.withdrawMinAmount || 0).toFixedMaxCy(8)}</li>
                                            </ul>
                                        })}
                                    </div>
                                    <div>
                                    {`${Big(item?.maxWithdrawAmount || 0).toFixedMaxCy(8)}/${Big(item?.dailyWithdrawAmount || 0).toFixedMaxCy(8)}`}
                                    </div>
                                    
                                </div>
                            })}
                        </div>
                    </XtPane>
                </div> */}
            </Tabs.TabPane>
        </Tabs>
    );

    const futureVipTable = (
        <Tabs defaultActiveKey="1" >
            <Tabs.TabPane tab={t("futureFee")} key="1">
                
                    <div className={`${cs.table} ${cs.contractTable}`}>
                        <ul className={cs.header}>
                            <li>{ t("group.Level") }</li>
                            <li>{ t("rate.monthFutureValue") }</li>
                            <li>
                                <span className={cs.makerTakerTips}>{ t("rate.makerRate") }</span>
                                <Popover content={makerContent} overlayClassName="contractTableTip" placement="left" trigger="hover">
                                    <span className={cs.appMakerTakerTips}>
                                        { t("rate.makerRate") }
                                    </span>
                                </Popover>
                            </li>
                            <li>
                                <span className={cs.makerTakerTips}>{ t("rate.takerRate") }</span>
                                <Popover content={takerContent} overlayClassName="contractTableTip" placement="left" trigger="hover">
                                    <span className={cs.appMakerTakerTips}>
                                        { t("rate.takerRate") }
                                    </span>
                                </Popover>
                            </li>
                        </ul>
                        <XtPane minHeight={930} nodata={!Array.isArray(fuVipList) || fuVipList.length <= 0}>
                            <div className={cs.tbody}>
                                { Array.isArray(fuVipList) && fuVipList.map((item, index) => {
                                    
                                    return <ul key={index}>
                                        <li>{item.stepName}</li>
                                        <li><span>≥</span>{item.tradeVolumeStr}</li>
                                        <li>{item.makerFeeStr}%</li>
                                        <li>{item.takerFeeStr}%</li>
                                    </ul>
                                })}
                                {/* <p className={cs.predictExclude}>{t('rate.predictExclude')}</p> */}
                            </div>
                        </XtPane>
                    </div>
            </Tabs.TabPane>
        </Tabs>
    );

    if (layout === "singlePage") {
        return <div className={cs.main}>{singlePageContent}</div>;
    }

    return (
        <div className={cs.main}>
            <div className={cs.tipPane}><div><SvgIcon icon="alert" style={{fontSize:"20px"}}></SvgIcon></div>{+rateType ? t("rate.contractTableTips") : t("rate.tableTips")}</div>
            <div className={cs.content}>
                <div className={cs.tabTips}>
                    {t("group.WhatAreMaker")}
                    <Popover content={makerTakerContent} overlayClassName="contractTableTip" placement="left" trigger="hover">
                        <span>
                            <SvgIcon icon="faq" style={{color: "rgba(0, 0, 0, 0.2)", fontSize: "16px"}}></SvgIcon>
                        </span>
                    </Popover>
                </div>
                <div className={cs.tabAppTips}>
                    <Popover content={+rateType ? t("rate.contractTableTips") : t("rate.tableTips")} overlayClassName="contractTableTip" placement="leftTop" trigger="hover">
                        <span>
                            <SvgIcon icon="faq" style={{color: "rgba(0, 0, 0, 0.2)", fontSize: "16px"}}></SvgIcon>
                        </span>
                    </Popover>
                </div>
                
                {+rateType ? futureVipTable : normalVipTable }
            </div>
        </div>
    );
};

export default observer(RateTable);